'use client';

import { useCallback, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Button, Box, type SxProps, type Theme } from '@mui/material';
import { Description } from '@mui/icons-material';
import BrochureDownloadModal from './BrochureDownloadModal';
import {
  getBrochureDownloadUrl,
  getHeroResourceDocumentsForPage,
  prefetchBrochureSubmitContext,
  submitBrochureLeadAndResolveUrl,
  type BrochureDownloadFlowResult,
  type BrochureDownloadFormValues,
  type PublicBrochureDocument,
} from '../services/brochureDownloadService';
import { getResourceDocumentCopy } from '../utils/resourceDocumentCopy';

const BRAND_GREEN = '#63A821';
const BRAND_GREEN_HOVER = '#55901d';

const sharedButtonSx: SxProps<Theme> = {
  color: '#fff',
  px: 3,
  py: 1.25,
  minHeight: 46,
  fontWeight: 600,
  fontSize: '0.875rem',
  borderRadius: '6px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  fontFamily: 'Poppins, sans-serif',
  boxShadow: 'none',
  border: 'none',
};

const whitePaperButtonSx: SxProps<Theme> = {
  ...sharedButtonSx,
  bgcolor: BRAND_GREEN,
  '&:hover': { bgcolor: BRAND_GREEN_HOVER },
};

const executiveSummaryButtonSx: SxProps<Theme> = {
  ...sharedButtonSx,
  bgcolor: BRAND_GREEN,
  '&:hover': { bgcolor: BRAND_GREEN_HOVER },
};

type ResourceButtonVariant = 'whitePaper' | 'executiveSummary';

export default function FeatureResourceButtons() {
  const pathname = usePathname();
  const [whitePaperDocument, setWhitePaperDocument] = useState<PublicBrochureDocument | null>(
    null,
  );
  const [executiveSummaryDocument, setExecutiveSummaryDocument] =
    useState<PublicBrochureDocument | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeDocument, setActiveDocument] = useState<PublicBrochureDocument | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    setWhitePaperDocument(null);
    setExecutiveSummaryDocument(null);

    getHeroResourceDocumentsForPage(pathname)
      .then(({ whitePaper, executiveSummary }) => {
        if (cancelled) return;

        const validWhitePaper =
          whitePaper && getBrochureDownloadUrl(whitePaper) ? whitePaper : null;
        const validExecutiveSummary =
          executiveSummary && getBrochureDownloadUrl(executiveSummary)
            ? executiveSummary
            : null;

        setWhitePaperDocument(validWhitePaper);
        setExecutiveSummaryDocument(validExecutiveSummary);
      })
      .catch(() => {
        if (cancelled) return;
        setWhitePaperDocument(null);
        setExecutiveSummaryDocument(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [pathname]);

  const handleOpen = (document: PublicBrochureDocument) => {
    prefetchBrochureSubmitContext();
    setActiveDocument(document);
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setActiveDocument(null);
  };

  const handleSubmit = useCallback(
    async (form: BrochureDownloadFormValues): Promise<BrochureDownloadFlowResult> => {
      if (!activeDocument || !getBrochureDownloadUrl(activeDocument)) {
        const copy = getResourceDocumentCopy(activeDocument?.document_type);
        throw new Error(copy.unavailableMessage);
      }
      return submitBrochureLeadAndResolveUrl(form, activeDocument, pathname);
    },
    [activeDocument, pathname],
  );

  if (isLoading || (!whitePaperDocument && !executiveSummaryDocument)) {
    return null;
  }

  const renderButton = (
    variant: ResourceButtonVariant,
    document: PublicBrochureDocument,
    label: string,
    sx: SxProps<Theme>,
  ) => (
    <Button
      key={variant}
      variant="contained"
      size="large"
      startIcon={<Description />}
      onClick={() => handleOpen(document)}
      sx={sx}
    >
      {label}
    </Button>
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          alignItems: 'center',
          justifyContent: 'center',
          width: { xs: '100%', sm: 'auto' },
        }}
      >
        {whitePaperDocument
          ? renderButton(
              'whitePaper',
              whitePaperDocument,
              'WHITE PAPER',
              whitePaperButtonSx,
            )
          : null}
        {executiveSummaryDocument
          ? renderButton(
              'executiveSummary',
              executiveSummaryDocument,
              'EXECUTIVE SUMMARY',
              executiveSummaryButtonSx,
            )
          : null}
      </Box>

      {activeDocument ? (
        <BrochureDownloadModal
          open={modalOpen}
          onClose={handleClose}
          document={activeDocument}
          featureMenuUrl={pathname}
          onSubmit={handleSubmit}
        />
      ) : null}
    </>
  );
}
